import React, { useEffect, useState } from 'react';
import { UserInput } from '../common/UserInput';
import ActionButton from '../buttons/ActionButton';
import Form from '../common/Form';
import apiService from 'services/apiService';
import { useAlert } from 'contexts/AlertContext';

import 'view/style/landing/demoRequestForm.css';

// URL for Google Calendar embedded scheduler
const GOOGLE_EMBED_URL =
  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ16wGT_8X2lZpxx_JtjSC9r5ck4DRudukclEktMlfwva65V8ovKY5KIWKP714rD8XGJV7927gtd?gv=true';

type DemoRequestData = {
  name: string;
  email: string;
  phoneNumber?: string;
  school?: string;
  message?: string;
};

type DemoFormProps = {
  onSuccess: (data: DemoRequestData) => void;
};

// Component for the initial demo request form
const DemoForm: React.FC<DemoFormProps> = ({ onSuccess }) => {
  const [formData, setFormData] = useState<DemoRequestData>({
    name: '',
    email: '',
    phoneNumber: undefined,
    school: undefined,
    message: undefined,
  });
  const { setAlert } = useAlert();

  useEffect(() => {
    return () => {
      setAlert(undefined);
    };
    // eslint-disable-next-line
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (formData.name === '' || formData.email === '') {
      setAlert({
        type: 'danger',
        silent: true,
        message: 'Name and email are required',
      });
      return;
    }
    apiService.api.post('/request_demo/', formData).then(() => {
      setAlert({
        type: 'success',
        silent: true,
        message:
          "We have received your request. Thank you for your interest, we'll get back to you as soon as possible!",
      });
      onSuccess(formData);
    });
  };

  return (
    <Form
      style={{ width: 'min(600px, 100vw)', minWidth: 'min(600px, 100vw)' }}
      onSubmit={handleSubmit}
    >
      <UserInput
        id="name"
        type="text"
        label="Name"
        value={formData.name}
        onChange={handleChange}
      />
      <UserInput
        id="email"
        type="email"
        label="Email"
        value={formData.email}
        onChange={handleChange}
      />
      <UserInput
        id="phoneNumber"
        type="text"
        label="Phone Number (optional)"
        value={formData.phoneNumber || ''}
        onChange={handleChange}
      />
      <UserInput
        id="school"
        type="text"
        label="School (optional)"
        value={formData.school || ''}
        onChange={handleChange}
      />
      <UserInput
        id="message"
        type="textarea"
        label="Put any questions, notes or concerns here (optional)"
        value={formData.message || ''}
        onChange={handleChange}
      />
      <ActionButton buttonType="submit" type="go" label="Go to scheduler" />
    </Form>
  );
};

type SchedulerEmbedProps = {
  onBack?: () => void;
};

// Component for embedding the Google Calendar scheduler
const SchedulerEmbed: React.FC<SchedulerEmbedProps> = ({ onBack }) => (
  <div className="scheduler-embed">
    <iframe
      src={GOOGLE_EMBED_URL}
      style={{ border: 0, width: '100%', height: 600 }}
      title="Google calendar scheduler"
    />
    <p>
      <i>
        If you don't want to schedule your demo now, or would prefer to have us
        contact you by email first, just click the 'X' in the top right and
        we'll reach out as soon as possible!
      </i>
    </p>
  </div>
);

// Parent component that switches between form and scheduler
const DemoRequestForm: React.FC = () => {
  const [showScheduler, setShowScheduler] = useState(false);

  return showScheduler ? (
    <SchedulerEmbed onBack={() => setShowScheduler(false)} />
  ) : (
    <DemoForm onSuccess={() => setShowScheduler(true)} />
  );
};

export default DemoRequestForm;
